// Kinio Node〔終端機〕（SPEC #node-ttl-vms-terminal、#node-ttl-relay、KN-T26 / T29 / T31）：經平台中繼，任何網路都能讀寫節點 UART1 接的設備。
// 瀏覽器開不了 raw TCP，只走中繼；選路不讓使用者選（#node-ttl-auto-route）。真正的終端機模擬（xterm）：按鍵直接送、輸出照原始位元組顯示。
// 有 device.read 就能唯讀連線；寫入只限管理角色（Eddy 2026-10-14 選單，SPEC #node-ttl-console-writer）：票的回應帶 can_write，
// false 時不給〔取得寫入權…〕與〔自己連線〕、寫「需要管理員才能輸入」；要 write 票被拒（403）就回到唯讀並白話提示。
// 同一時間一個寫入者，別人在寫時按〔取得寫入權〕搶過來（take_write），兩邊畫面靠 role 幀更新。
// VMS UX 評審第三輪 R3-P0-4、B3-06：放在設備頁「進階 › 診斷」；進來**不連線、不要票**，按〔連線（唯讀）〕才要 read 票；
// 要輸入按〔取得寫入權…〕→ 第 2 級確認 → 換 write 票重連（別人在寫就送 take_write）；〔交出寫入權〕換回 read 票。連上時寫出另外幾人連線中。
// 〔複製連線指令〕給 nodectl tunnel 指令（不放 IP / token / 金鑰）；面板最下面「進階：用自己的工具直接連」才拿直連 token 與 IP（#node-ttl-direct）。
// 第四輪 B4-14、R4-P1-6：沒連線時只有說明與一排次要按鈕，不畫空的終端機框；按了連線才建 xterm（斷線後保留畫面）。
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import '@xterm/xterm/css/xterm.css';
import { KinioError } from '@kinio/web-sdk';
import { useConfirm } from '../../components/ConfirmDialog';
import { deviceApi } from '../../lib/deviceApi';
import type { VmsDevice } from '../../lib/devices';
import { amWriter, applyFrame, decodeBytes, encodeData, initialView, othersConnected, terminalHref, tunnelCommand, type ConsoleView } from './consoleRelay';
import { stamp } from '../../lib/timefmt';

type Direct = Awaited<ReturnType<typeof deviceApi.ttlDirectToken>>;

function cssVar(name: string, fallback: string): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

export function NodeTerminal({ dev, standalone = false }: { dev: VmsDevice; standalone?: boolean }) {
  const { t } = useTranslation();
  const [v, setV] = useState<ConsoleView>(() => initialView());
  const [panel, setPanel] = useState(false);
  const [directOpen, setDirectOpen] = useState(false);
  const [copied, setCopied] = useState('');
  const [directOk, setDirectOk] = useState(false);
  const [direct, setDirect] = useState<Direct | null>(null);
  const [directErr, setDirectErr] = useState('');
  /** 平台說這個人能不能寫（read 票回應的 can_write）；null = 還沒連過、不知道 */
  const [canWrite, setCanWrite] = useState<boolean | null>(null);
  /** write 票被拒（403）：回到唯讀並提示 */
  const [writeDenied, setWriteDenied] = useState(false);
  const host = useRef<HTMLDivElement | null>(null);
  const term = useRef<Terminal | null>(null);
  const ws = useRef<WebSocket | null>(null);
  const writerRef = useRef(false);
  const userClosed = useRef(false);
  const retried = useRef(false);
  const wasOpen = useRef(false);
  /** 這次 write 票是使用者確認要寫才拿的：hello 時別人在寫就送 take_write */
  const wantTake = useRef(false);
  const { ask, dialog } = useConfirm();

  const writer = amWriter(v);
  writerRef.current = writer;

  const open = useCallback(async (mode: 'read' | 'write' = 'read') => {
    userClosed.current = false;
    wasOpen.current = false;
    setV({ ...initialView(mode), status: 'ticket' });
    try {
      const tk = await deviceApi.consoleTicket(dev.apiEndpoint, dev.homeId, dev.aid, mode);
      if (userClosed.current) return;
      setCanWrite(tk.can_write !== false);
      const sock = new WebSocket(tk.ws_url);
      ws.current = sock;
      setV((p) => ({ ...p, status: 'connecting', mode: tk.mode }));
      sock.onmessage = (ev) => {
        const raw = String(ev.data);
        if (raw.includes('"data"')) {
          try {
            const m = JSON.parse(raw) as { type?: string; data?: string };
            if (m.type === 'data' && m.data) term.current?.write(decodeBytes(m.data));
          } catch {
            /* applyFrame 一樣會略過 */
          }
        }
        if (wantTake.current && raw.includes('"hello"')) {
          // 使用者已確認要寫：write 票在沒人寫時平台自動給寫入權；別人在寫就搶過來
          wantTake.current = false;
          try {
            const h = JSON.parse(raw) as { type?: string; client_id?: string; writer?: string };
            if (h.type === 'hello' && h.writer && h.writer !== h.client_id) sock.send(JSON.stringify({ type: 'take_write' }));
          } catch {
            /* 壞幀略過 */
          }
        }
        setV((p) => {
          const n = applyFrame(p, raw, { skipOut: true });
          if (n.status === 'open') wasOpen.current = true;
          return n;
        });
      };
      sock.onclose = () => {
        if (ws.current !== sock) return;
        ws.current = null;
        setV((p) => ({ ...p, status: 'closed', me: '', writer: p.writer }));
        // 意外斷線（不是使用者按關）：連上過的話自動要新票重連一次，再斷就停在〔重新連線〕
        if (!userClosed.current && wasOpen.current && !retried.current) {
          retried.current = true;
          term.current?.write(`\r\n\x1b[2m[${t('ncon.auto_reconnect')}]\x1b[0m\r\n`);
          void open(mode);
        }
      };
    } catch (e) {
      const code = e instanceof KinioError ? e.code : 'NETWORK';
      if (mode === 'write' && code === 'FORBIDDEN') {
        // 角色不能寫（例：剛被收回）：不留在斷線，回到唯讀
        wantTake.current = false;
        setCanWrite(false);
        setWriteDenied(true);
        void open('read');
        return;
      }
      setV((p) => ({ ...p, status: 'closed', reason: code }));
    }
  }, [dev.apiEndpoint, dev.homeId, dev.aid, t]);

  const close = () => {
    userClosed.current = true;
    ws.current?.close();
  };
  const reconnect = (mode: 'read' | 'write' = 'read') => {
    retried.current = false;
    const old = ws.current;
    ws.current = null; // 舊連線的 onclose 不再動畫面
    old?.close();
    void open(mode);
  };
  /** 〔取得寫入權…〕：第 2 級確認後換 write 票（已經是 write 票、只是別人拿走了 → 直接 take_write）。 */
  const takeWrite = async () => {
    const others = othersConnected(v);
    const ok = await ask({
      title: t('ncon.take_confirm_title', { name: dev.name }),
      body: (
        <>
          <p>{t('ncon.take_confirm_body')}</p>
          {(others > 0 || (v.writer && v.writer !== v.me)) && <p className="warn-text">{t('ncon.take_confirm_others')}</p>}
        </>
      ),
      action: t('ncon.take'),
    });
    if (!ok) return;
    if (v.mode === 'write' && ws.current && v.status === 'open') {
      ws.current.send(JSON.stringify({ type: 'take_write' }));
      return;
    }
    wantTake.current = true;
    reconnect('write');
  };
  /** 〔交出寫入權〕：放掉寫入權並換回 read 票（之後按鍵不送）。 */
  const releaseWrite = () => {
    ws.current?.send(JSON.stringify({ type: 'release_write' }));
    reconnect('read');
  };
  const send = (data: string) => ws.current?.send(JSON.stringify({ type: 'input', data: encodeData(data) }));

  // 連過一次才有終端機框（B4-14：沒連線不畫空的黑框）；之後斷線也保留畫面
  const shown = v.status !== 'idle';

  // xterm：第一次連線時建一次；大小跟著容器（UART 沒有 TTY 大小，不回報給設備）
  useEffect(() => {
    if (!shown || !host.current) return;
    const x = new Terminal({
      scrollback: 5000,
      cursorBlink: true,
      fontFamily: cssVar('--mono', 'ui-monospace, Menlo, monospace'),
      fontSize: 13,
      theme: { background: cssVar('--surface-2', '#1d232c'), foreground: cssVar('--text', '#e6edf3'), cursor: cssVar('--accent', '#0b7a75') },
    });
    const fit = new FitAddon();
    x.loadAddon(fit);
    x.open(host.current);
    fit.fit();
    x.onData((d) => {
      if (writerRef.current) send(d);
    });
    term.current = x;
    const ro = new ResizeObserver(() => {
      try {
        fit.fit();
      } catch {
        /* 容器還沒有大小 */
      }
    });
    ro.observe(host.current);
    return () => {
      ro.disconnect();
      x.dispose();
      term.current = null;
    };
  }, [shown]);

  // 進來不連線（R3-P0-4：按〔連線（唯讀）〕才要票）；離開分頁 / 換設備就關（ticket 一次性，不留背景連線）
  useEffect(() => {
    retried.current = false;
    term.current?.reset();
    return () => {
      userClosed.current = true;
      ws.current?.close();
      ws.current = null;
    };
  }, [open]);

  useEffect(() => {
    if (standalone) document.title = `${dev.name} · ${t('ncon.title')}`;
  }, [standalone, dev.name, t]);

  const live = !!ws.current && v.status !== 'closed' && v.status !== 'idle';
  const noWrite = canWrite === false;
  const others = othersConnected(v);
  const busy = v.status === 'ticket' || v.status === 'connecting';
  const cmd = tunnelCommand(dev.aid, dev.homeId);
  const copy = (what: string, text: string) => {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(what);
      setTimeout(() => setCopied(''), 1500);
    });
  };
  const getDirect = async () => {
    setDirectErr('');
    try {
      setDirect(await deviceApi.ttlDirectToken(dev.apiEndpoint, dev.homeId, dev.aid));
    } catch (e) {
      setDirectErr(e instanceof KinioError ? e.code : 'NETWORK');
    }
  };

  return (
    <div className={`node-term${standalone ? ' standalone' : ''}`} data-node-console={dev.aid} data-readonly={live && !writer ? 'true' : 'false'} data-shown={shown ? 'true' : 'false'}>
      {v.status === 'idle' && (
        <p className="small" data-testid="ncon-intro">
          {t('ncon.intro')}
        </p>
      )}
      <div className="row node-term-bar">
        <span className="small" data-testid="ncon-status" data-status={v.status}>
          <span className={`dot ${v.status === 'open' ? 'online' : v.status === 'closed' ? 'offline' : ''}`} />
          {t(`ncon.status_${v.status}`)}
          {v.reason ? ` · ${t(`ncon.reason_${v.reason}`, { defaultValue: t('common.state_unknown') })}` : ''}
          {v.status === 'open' && <span className="muted"> · {t('ncon.via_platform')}</span>}
        </span>
        {live && v.me && (
          <span className="small muted" data-testid="ncon-role" data-writer={writer ? 'me' : v.writer ? 'other' : 'none'}>
            {writer ? t('ncon.role_me') : v.writer ? t('ncon.role_other') : t('ncon.role_none')}
          </span>
        )}
        {live && v.me && others > 0 && (
          <span className="small warn-text" data-testid="ncon-others" data-n={others}>
            {t('ncon.others_n', { count: others })}
          </span>
        )}
        {live && v.me && !writer && !noWrite && (
          <button className="btn btn-sm" onClick={() => void takeWrite()} data-testid="ncon-take">
            {t('ncon.take_ellipsis')}
          </button>
        )}
        {live && writer && (
          <button className="btn btn-sm" onClick={releaseWrite} data-testid="ncon-release">
            {t('ncon.release')}
          </button>
        )}
        <span style={{ flex: 1 }} />
        {shown && (
          <button className="btn btn-sm" onClick={() => term.current?.clear()} data-testid="ncon-clear">
            {t('ncon.clear')}
          </button>
        )}
        {live ? (
          <button className="btn btn-sm" onClick={close} data-testid="ncon-close">
            {t('ncon.close')}
          </button>
        ) : (
          <button className="btn btn-sm" onClick={() => reconnect('read')} disabled={busy} data-testid="ncon-open">
            {v.status === 'idle' ? t('ncon.connect_ro') : t('ncon.reconnect')}
          </button>
        )}
        {!standalone && (
          <a
            className="btn btn-sm"
            href={terminalHref(dev.homeId, dev.aid)}
            onClick={(e) => {
              e.preventDefault();
              close(); // 新視窗自己開一條；這頁的關掉，免得兩條搶寫入權
              window.open(terminalHref(dev.homeId, dev.aid), `kinio-term-${dev.aid}`, 'popup,width=960,height=640');
            }}
            data-testid="ncon-popout"
          >
            {t('ncon.popout')}
          </a>
        )}
        <button className="btn btn-sm" aria-pressed={panel} aria-expanded={panel} onClick={() => setPanel(!panel)} data-testid="ncon-tunnel">
          {t('ncon.tunnel')}
        </button>
      </div>

      {panel && (
        <div className="card node-term-panel" data-testid="ncon-tunnel-panel">
          <p className="small">{t('ncon.tunnel_hint')}</p>
          <div className="row">
            <code className="mono" style={{ flex: 1, userSelect: 'all' }} data-testid="ncon-tunnel-cmd">
              {cmd}
            </code>
            <button className="btn btn-sm" onClick={() => copy('cmd', cmd)}>
              {copied === 'cmd' ? t('ncon.copied') : t('ncon.copy')}
            </button>
          </div>
          <ol className="small muted">
            <li>{t('ncon.tunnel_step_download')}</li>
            <li>
              {t('ncon.tunnel_step_key')} <Link to="/settings">{t('ncon.tunnel_key_link')}</Link>
            </li>
            <li>{t('ncon.tunnel_step_writer')}</li>
          </ol>
          {!noWrite && (
            <button className="link-btn small" style={{ alignSelf: 'flex-start' }} aria-expanded={directOpen} onClick={() => setDirectOpen(!directOpen)} data-testid="ncon-direct">
              {directOpen ? '▾' : '▸'} {t('ncon.direct')}
            </button>
          )}
        </div>
      )}

      {panel && directOpen && !noWrite && (
        <div className="card node-term-panel" data-testid="ncon-direct-panel">
          <p className="small">{t('ncon.direct_hint')}</p>
          {!direct ? (
            <>
              <label className="small">
                <input type="checkbox" checked={directOk} onChange={(e) => setDirectOk(e.target.checked)} data-testid="ncon-direct-confirm" /> {t('ncon.direct_confirm')}
              </label>
              <div className="row">
                <button className="btn btn-sm" disabled={!directOk} onClick={() => void getDirect()} data-testid="ncon-direct-get">
                  {t('ncon.direct_get')}
                </button>
                {directErr && <span className="small warn-text">{t(`ncon.reason_${directErr}`, { defaultValue: directErr })}</span>}
              </div>
            </>
          ) : (
            <dl className="small node-term-kv">
              <dt>{t('ncon.direct_addr')}</dt>
              <dd className="mono">{direct.lan ? `${direct.lan.ip}:${direct.lan.port}` : t('ncon.direct_no_lan')}</dd>
              <dt>{t('ncon.direct_token')}</dt>
              <dd className="mono">
                <code style={{ userSelect: 'all' }}>{direct.token}</code>{' '}
                <button className="btn btn-sm" onClick={() => copy('token', direct.token)}>
                  {copied === 'token' ? t('ncon.copied') : t('ncon.copy')}
                </button>
                <div className="muted">
                  {t('ncon.direct_token_once', { id: direct.token_id, at: stamp(direct.expires_at) })}
                </div>
              </dd>
            </dl>
          )}
        </div>
      )}

      {(v.lost > 0 || v.droppedFrames > 0 || v.inDropped > 0) && (
        <p className="small warn-text" data-testid="ncon-loss">
          {v.lost > 0 && t('ncon.lost', { n: v.lost })}
          {v.deviceRebooted && ` ${t('ncon.rebooted')}`}
          {v.droppedFrames > 0 && ` ${t('ncon.dropped', { n: v.droppedFrames })}`}
          {v.inDropped > 0 && ` ${t('ncon.in_dropped', { n: v.inDropped })}`}
        </p>
      )}
      {v.lastError && (
        <p className="small warn-text" data-testid="ncon-error">
          {t(`ncon.err_${v.lastError}`, { defaultValue: v.lastError })}
        </p>
      )}
      {writeDenied && (
        <p className="small warn-text" role="status" data-testid="ncon-write-denied">
          {t('ncon.write_forbidden')}
        </p>
      )}
      {live && v.me && !writer && !noWrite && <p className="small muted">{t('ncon.input_ro')}</p>}
      {live && v.me && !writer && noWrite && !writeDenied && (
        <p className="small muted" data-testid="ncon-need-admin">
          {t('ncon.need_admin')}
        </p>
      )}
      {dialog}
      {shown && <div ref={host} className="node-term-screen" data-testid="ncon-out" onClick={() => term.current?.focus()} />}
    </div>
  );
}
